import type { Placement } from "@classes-service-types/index.ts";
import { useMutation, useQueryClient } from "@tanstack/vue-query";
import type { PlacementRequest } from "@/components/MyStudentDetail/FilterableTodosSection/types";
import { getTodosSubmissionsQueryKey } from "@/providers/todosSubmissions/useGetTodosSubmissions";
import { profileServiceRequest } from "@/services/utils";

export type ApplicationTodoUpsertPayload = {
  display_name?: string | null;
  placement: Placement;
  supporting_url?: string | null;
  instructions?: Record<string, unknown> | null;
  ranking?: number | null;
  due_date?: string | null;
  required: boolean;
};

export type UpsertApplicationTodoArgs = {
  applicationId: string;
  todoId: string;
  payload: ApplicationTodoUpsertPayload;
  participantId?: string;
};

export type ApplicationTodoUpsertResponse = {
  data: {
    id: string;
    todo_id: string;
    application_id: string;
  } & Record<string, unknown>;
};

export type ArchiveApplicationTodoArgs = {
  applicationId: string;
  todoId: string;
  participantId?: string;
};

export const useApplicationTodo = () => {
  const queryClient = useQueryClient();

  const upsert = useMutation<ApplicationTodoUpsertResponse["data"], Error, UpsertApplicationTodoArgs>({
    mutationFn: async ({ applicationId, todoId, payload }) => {
      const response = await profileServiceRequest({
        method: "put",
        url: `/applications/${applicationId}/todos/${todoId}/`,
        payload: {
          ...payload,
          placement: payload.placement.toLowerCase() as PlacementRequest,
        },
      });
      const body: ApplicationTodoUpsertResponse = response?.data;
      return body?.data;
    },
    onSuccess: (_data, variables) => {
      if (variables.participantId) {
        queryClient.invalidateQueries({ queryKey: getTodosSubmissionsQueryKey(variables.participantId) });
      }
    },
  });

  const archive = useMutation<void, Error, ArchiveApplicationTodoArgs>({
    mutationFn: async ({ applicationId, todoId }) => {
      await profileServiceRequest({
        method: "delete",
        url: `/applications/${applicationId}/todos/${todoId}/`,
      });
    },
    onSuccess: (_data, variables) => {
      if (variables.participantId) {
        queryClient.invalidateQueries({ queryKey: getTodosSubmissionsQueryKey(variables.participantId) });
      }
    },
  });

  return { upsert, archive };
};
